import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Pagination, Empty, Button, Tag } from 'antd';
import {
  CalendarOutlined,
  FileTextOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import lib from '../../../assets/utils/lib';
import { notification } from 'antd';
import { useSearchParams } from 'react-router-dom';
import SearchInput from '../SearchInput';
import ClaimsSheetButton from './ClaimsSheetButton';
import { getClaimsArchiveScope, withClaimsArchiveScope } from '../claimsArchiveHelpers';
import {
  getClaimsApprovedTotal,
  getPaApprovedTotal,
  getSubmittedTotal,
} from '../claimFinancialHelpers';
import dayjs from 'dayjs';
import './PaymentStatusMonths.css';
import './PaymentDrilldown.css';

const STATUS_LABELS = {
  0: 'Pending CO',
  1: 'Pending HM',
  2: 'Pending CFO',
  3: 'Paid',
};

const STATUS_BRAND = {
  0: '#3497D3',
  1: '#faad14',
  2: '#722ed1',
  3: '#49B870',
};

function DrillRowSkeleton() {
  return (
    <div className="drill-row-skeleton" aria-hidden>
      <div className="drill-row-skeleton__block" style={{ width: 34, height: 34, borderRadius: 9 }} />
      <div className="drill-row-skeleton__block" style={{ width: '38%' }} />
      <div className="drill-row-skeleton__block" style={{ width: 60, marginLeft: 'auto' }} />
      <div className="drill-row-skeleton__block" style={{ width: 90 }} />
    </div>
  );
}

function getMonthKey(month) {
  if (!month) return null;
  if (typeof month === 'string') return month;
  return month.month || month.key || null;
}

function formatMonthLabel(month) {
  if (month?.month_label) return month.month_label;
  const key = getMonthKey(month);
  if (!key) return 'Month';
  return dayjs(`${key}-01`).format('MMMM YYYY');
}

function formatShortDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function stageDateLabel(code) {
  switch (Number(code)) {
    case 0: return 'Filed';
    case 1: return 'Forwarded to HM';
    case 2: return 'Forwarded to CFO';
    case 3: return 'Paid';
    default: return 'Stage';
  }
}

/**
 * PaymentStatusMonths — encounter months for a selected payment stage.
 */
const PaymentStatusMonths = ({ segment, onMonthSelect }) => {
  const [searchParams] = useSearchParams();
  const MONTHS_SEARCH_PARAM = 'paymentMonthsSearch';
  const MONTHS_PAGE_PARAM = 'paymentMonthsPage';
  const MONTHS_PAGE_SIZE_PARAM = 'paymentMonthsPageSize';
  const [months, setMonths] = useState([]);
  const [searchMeta, setSearchMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });
  const fetchingRef = useRef(false);

  const searchQuery = searchParams.get(MONTHS_SEARCH_PARAM) || '';
  const archiveScope = getClaimsArchiveScope(searchParams);
  const accent = STATUS_BRAND[segment] || STATUS_BRAND[0];
  const statusLabel = STATUS_LABELS[segment] || 'Status';

  const fetchMonths = useCallback(async (page = 1, search = '') => {
    if (segment === null || segment === undefined) return;
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    setLoading(true);
    try {
      const params = withClaimsArchiveScope({
        page,
        pageSize: pagination.pageSize,
        ...(search && search.trim() ? { search: search.trim() } : {}),
      }, archiveScope);

      const response = await lib.getPaymentStatusMonths(segment, params);

      if (response?.data?.error) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch months',
        });
        return;
      }

      const responseData = response?.data || {};
      const monthsList = Array.isArray(responseData.data) ? responseData.data : [];

      setMonths(monthsList);
      setSearchMeta(responseData.search || null);
      setPagination((prev) => ({
        ...prev,
        current: responseData.pagination?.page || 1,
        pageSize: responseData.pagination?.pageSize || prev.pageSize,
        total: responseData.pagination?.total || monthsList.length || 0,
      }));
    } catch (error) {
      console.error('Error fetching months:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to fetch months',
      });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [segment, pagination.pageSize, archiveScope]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (segment !== null && segment !== undefined) {
      const page = parseInt(searchParams.get(MONTHS_PAGE_PARAM) || '1', 10);
      fetchMonths(page, searchQuery);
    }
  }, [segment, searchQuery, fetchMonths, searchParams]);

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    fetchMonths(page, searchQuery);
    const newParams = new URLSearchParams(searchParams);
    newParams.set(MONTHS_PAGE_PARAM, page.toString());
    if (pageSize) {
      newParams.set(MONTHS_PAGE_SIZE_PARAM, pageSize.toString());
    }
    window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const clearSearch = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(MONTHS_SEARCH_PARAM);
    newParams.set(MONTHS_PAGE_PARAM, '1');
    window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
    handleSearch();
  };

  const handleMonthClick = (month) => {
    onMonthSelect?.(month);
  };

  if (segment === null || segment === undefined) {
    return <Empty description="No status selected" />;
  }

  return (
    <div className="payment-status-months">
      <div className="drill-panel">
        <div className="drill-panel__head">
          <div>
            <h2>Encounter months</h2>
            <p>
              {loading ? 'Loading…' : `${pagination.total.toLocaleString()} month${pagination.total === 1 ? '' : 's'}`}
              {' '}· click a month to see its facilities
            </p>
          </div>
          <div className="payment-months-panel__actions">
            <SearchInput
              placeholder="Search months (e.g. 2024, January)"
              onSearch={handleSearch}
              loading={loading}
              searchParamName={MONTHS_SEARCH_PARAM}
              pageParamName={MONTHS_PAGE_PARAM}
            />
            <ClaimsSheetButton segment={segment} level="months" size="middle" disabled={loading} />
          </div>
        </div>

        {searchMeta?.applied ? (
          <div className="payment-months-search-banner" role="status">
            <span>
              Found <strong>{searchMeta.results_count || 0}</strong> result
              {searchMeta.results_count !== 1 ? 's' : ''} for{' '}
              <Tag color="blue">{searchMeta.query}</Tag>
            </span>
            <button type="button" className="payment-months-search-banner__clear" onClick={clearSearch}>
              <CloseOutlined aria-hidden /> Clear
            </button>
          </div>
        ) : null}

        <div className="drill-scroll">
        {!loading ? (
          <div className="drill-cols">
            <span className="c-main">Month</span>
            <span className="c-num">Facilities</span>
            <span className="c-num">Claims</span>
            <span className="c-num">Manual</span>
            <span className="c-num">Direct</span>
            <span className="c-money">Submitted</span>
            <span className="c-money">PA approved</span>
            <span className="c-money">Claims approved</span>
            <span className="c-chev" />
          </div>
        ) : null}

        {loading ? (
          <div aria-label="Loading months">
            {Array.from({ length: 5 }).map((_, i) => (
              <DrillRowSkeleton key={`skel-${i}`} />
            ))}
          </div>
        ) : months.length === 0 ? (
          <div className="claims-empty">
            <FileTextOutlined style={{ fontSize: 28, color: '#94a3b8' }} />
            <p className="claims-empty__title">
              {searchQuery ? `No months found for "${searchQuery}"` : 'No months in this stage'}
            </p>
            <p className="claims-empty__desc">
              {searchQuery
                ? 'Try a different year or month name.'
                : `No encounter months are available for ${statusLabel}.`}
            </p>
          </div>
        ) : (
          months.map((month) => {
            const stageFrom = formatShortDate(month.earliest_stage_date);
            const stageTo = formatShortDate(month.latest_stage_date);
            return (
              <button
                type="button"
                key={month.month}
                className="drill-row"
                onClick={() => handleMonthClick(month)}
              >
                <div className="drill-row__main">
                  <span className="drill-row__icon" style={{ background: `${accent}1a`, color: accent }}>
                    <CalendarOutlined />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="drill-row__title">{formatMonthLabel(month)}</div>
                    <div className="drill-row__sub">
                      {stageFrom || stageTo ? `${stageDateLabel(segment)} ${stageFrom || '—'} – ${stageTo || '—'}` : (month.month || '')}
                    </div>
                  </div>
                </div>
                <span className="drill-row__num">{(month.facilities_count || 0).toLocaleString()}</span>
                <span className="drill-row__num">{(month.claims_count || 0).toLocaleString()}</span>
                <span className="drill-row__num">{Number(month.manual_items_count || 0).toLocaleString()}</span>
                <span className="drill-row__num">{Number(month.direct_items_count || 0).toLocaleString()}</span>
                <span className="drill-row__money">
                  <CurrencyFormat value={getSubmittedTotal(month)} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                </span>
                <span className="drill-row__money">
                  <CurrencyFormat value={getPaApprovedTotal(month)} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                </span>
                <span className="drill-row__money is-accent">
                  <CurrencyFormat value={getClaimsApprovedTotal(month)} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                </span>
                <span className="drill-row__chev">›</span>
              </button>
            );
          })
        )}
        </div>

        {!loading && pagination.total > pagination.pageSize ? (
          <div className="payment-months-pagination">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={handlePageChange}
              showSizeChanger={false}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PaymentStatusMonths;
